import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchPatient } from "../../store/slices/patientsSlice";
import { CLIENT_SECTIONS, LIKERT_LEGEND } from "./clientFeedbackConfig";

const calculateAge = (dob) => {
  if (!dob) return "";
  const b = new Date(dob);
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age;
};

// A Likert 1–5 radio row. Rendered inline via .map (no inner component) so
// inputs keep focus. Uses setRating passed from the parent.
const ClientFeedbackForm = ({ formData, setFormData, readOnly = false }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData.patient_id && !formData.client_name) {
      dispatch(fetchPatient(formData.patient_id))
        .unwrap()
        .then((p) => {
          setFormData((prev) => ({
            ...prev,
            client_name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
            age: p.dob ? String(calculateAge(p.dob)) : prev.age,
            gender: p.gender || prev.gender,
          }));
        })
        .catch((err) => console.error("Failed to prefill patient:", err));
    }
  }, [dispatch, formData.patient_id, formData.client_name, setFormData]);

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setRating = (key, val) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
      {/* Likert legend */}
      <div className="alert alert-light border mb-3">
        <div className="fw-bold mb-1">Likert Scale Guide</div>
        <div className="row g-1 small">
          {LIKERT_LEGEND.map((l) => (
            <div className="col-md-12" key={l.n}>
              <strong>{l.n} = {l.label}</strong> <span className="text-muted">({l.desc})</span>
            </div>
          ))}
        </div>
        <div className="small text-muted mt-2">
          Note: Please select the number that best represents your experience for each question.
        </div>
      </div>

      <div className="accordion" id="clientFeedbackAccordion">
        {/* Basic info */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#cfBasic">
              Client Satisfaction Report — Basic Information
            </button>
          </h2>
          <div id="cfBasic" className="accordion-collapse collapse show">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client Name</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="age" value={formData.age || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Age</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="gender" value={formData.gender || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Gender</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_admission" value={formData.date_of_admission || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Admission</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_discharge" value={formData.date_of_discharge || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Discharge</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="interviewer_name" value={formData.interviewer_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Interviewer Name</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Likert sections */}
        {CLIENT_SECTIONS.map((section, sIdx) => (
          <div className="accordion-item mb-3 shadow-sm rounded" key={section.title}>
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#cfSec${sIdx}`}>
                {section.title}
              </button>
            </h2>
            <div id={`cfSec${sIdx}`} className="accordion-collapse collapse">
              <div className="accordion-body pt-3">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Question</th>
                        <th className="text-center" style={{ width: "8%" }}>1</th>
                        <th className="text-center" style={{ width: "8%" }}>2</th>
                        <th className="text-center" style={{ width: "8%" }}>3</th>
                        <th className="text-center" style={{ width: "8%" }}>4</th>
                        <th className="text-center" style={{ width: "8%" }}>5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.questions.map((q) => (
                        <tr key={q.key}>
                          <td className="small">{q.label}</td>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <td className="text-center" key={n}>
                              <input
                                type="radio"
                                className="form-check-input"
                                name={q.key}
                                checked={Number(formData[q.key]) === n}
                                onChange={() => setRating(q.key, n)}
                                disabled={readOnly}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Section O: Additional Feedback */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#cfAdditional">
              Section O: Additional Feedback
            </button>
          </h2>
          <div id="cfAdditional" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <label className="form-label">Please share any suggestions or comments to help us improve:</label>
              <textarea className="form-control" rows="5" name="additional_feedback" value={formData.additional_feedback || ""} onChange={handleChange} disabled={readOnly}></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientFeedbackForm;
